import * as Yup from 'yup';
import { FieldMetaType, RegionMetaType, VaultHolderType, VaultRegionType } from '@/types/myVaultTypes';

export const OTHER_BANK = '__other';
export const STEPS = ['Region', 'Bank', 'Transfer types', 'Details', 'Saved'] as const;

export type CreateVaultAccountFormType = {
  region: VaultRegionType;
  bank: string;
  otherBank: string;
  rails: string[];
  holder_type: VaultHolderType;
  nickname: string;
  fields: Record<string, string>;
};

export const CREATE_VAULT_ACCOUNT_INITIAL_VALUES: CreateVaultAccountFormType = {
  region: 'US',
  bank: '',
  otherBank: '',
  rails: [],
  holder_type: 'business',
  nickname: '',
  fields: {},
};

export const neededFields = (meta: RegionMetaType, rails: string[]): FieldMetaType[] =>
  meta.fields.filter((field) => field.rails.some((rail) => rails.includes(rail)));

const NORMALIZE_SPACES = ['iban', 'account_number', 'sort_code'];

export const fieldsSchema = (fields: Pick<FieldMetaType, 'key' | 'label' | 'pattern' | 'optional'>[]) =>
  Yup.object(
    Object.fromEntries(
      fields.map((field) => {
        let rule = Yup.string().trim().max(255, 'Too long');

        if (!field.optional) rule = rule.required(`${field.label} is required.`);

        if (field.pattern) {
          const re = new RegExp(field.pattern);

          rule = rule.test('format', `${field.label} is not in the expected format.`, (value) => {
            if (!value) return field.optional;
            const normalized = NORMALIZE_SPACES.includes(field.key) ? value.replace(/\s/g, '') : value;

            return re.test(
              ['swift', 'bic', 'iban', 'currency'].includes(field.key) ? normalized.toUpperCase() : normalized
            );
          });
        }

        return [field.key, rule];
      })
    )
  );

export const getCreateVaultAccountSchema = (step: number, meta: RegionMetaType, rails: string[]) => {
  if (step === 2) {
    return Yup.object({
      bank: Yup.string().required('Choose a bank.'),
      otherBank: Yup.string().when('bank', {
        is: OTHER_BANK,
        then: (s) => s.trim().required("Type your bank's name."),
      }),
    });
  }

  if (step === 3) {
    return Yup.object({ rails: Yup.array().of(Yup.string()).min(1, 'Pick at least one transfer type.') });
  }

  if (step === 4) {
    return Yup.object({
      nickname: Yup.string().trim().required('Give the account a nickname.').max(255),
      fields: fieldsSchema(neededFields(meta, rails)),
    });
  }

  return Yup.object({});
};
